import { GitMerge, type LucideIcon, Scissors } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { ErrorState } from "../../ui";

type Kind = "missed" | "suspect";

// Mirrors GroupingReview.tsx's local Member/Candidate shapes — that file is
// kept as-is (it stays the Manage backlog at /manage/grouping), so this is a
// deliberate duplicate rather than a shared import.
type GroupingMember = {
	productId: number;
	storeId: string;
	title: string;
	normalizedTitle: string | null;
	ean: string | null;
	imageUrl: string | null;
	priceMinor: number | null;
	inStock: boolean | null;
	url: string;
	groupKey: string;
};

type GroupingCandidate = {
	kind: Kind;
	signature: string;
	members: GroupingMember[];
};

// Scoped to this group with `group=`: the unscoped answer is the whole
// backlog (~1.6MB on a 50k-product catalogue), of which this panel renders
// only the candidates naming one of its own variants — the filtering below
// still runs, it just no longer has a catalogue to wade through.
async function fetchCandidates(kind: Kind, groupKey: string): Promise<GroupingCandidate[]> {
	const res = await fetch(`/api/grouping/review?kind=${kind}&group=${encodeURIComponent(groupKey)}`);
	if (!res.ok) throw new Error(`HTTP ${res.status}`);
	const data = (await res.json()) as { candidates: GroupingCandidate[] };
	return data.candidates;
}

async function postJson(path: string, body: unknown): Promise<void> {
	const res = await fetch(path, {
		method: "POST",
		headers: { "content-type": "application/json" },
		body: JSON.stringify(body),
	});
	if (!res.ok) {
		const j = (await res.json().catch(() => ({}))) as { error?: string };
		throw new Error(j.error ?? `HTTP ${res.status}`);
	}
}

// The server hands back terse, lowercase reasons ("not found", "HTTP 404",
// "productIds requires at least 2 items") meant for logs, not the operator.
// Every catch below leads with a stable sentence naming the failure and
// keeps the raw reason as parenthetical detail, matching the pattern
// Items.tsx's fetchVariants already uses for the same kind of raw server text.
function reasonOf(e: unknown): string {
	return (e instanceof Error && e.message) || "request failed";
}

/**
 * Row-level grouping actions, scoped to the product already open on this
 * page. The full backlog of candidates still lives at /manage/grouping
 * (GroupingReview); this surfaces only what concerns this group, on the row
 * it concerns, so the operator never has to leave the product to act on it.
 *
 * Both actions are destructive-ish and irreversible from the UI, so each
 * requires a second tap to confirm — and neither spends a state hue to stand
 * out, since grouping is not product state. They're distinguished by weight
 * and border instead (see TwoStepAction below).
 */
export function GroupingActions({
	groupKey,
	productIds,
	groupEan,
	onChanged,
}: {
	groupKey: string;
	productIds: number[];
	/** Shown so the operator can read it against each candidate member's own
	 *  EAN below without having to scroll back up to the product header. */
	groupEan?: string | null;
	onChanged: () => void;
}) {
	const [missed, setMissed] = useState<GroupingCandidate[] | null>(null);
	const [suspect, setSuspect] = useState<GroupingCandidate[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState<string | null>(null);

	const load = useCallback(async () => {
		setError(null);
		try {
			const [m, s] = await Promise.all([fetchCandidates("missed", groupKey), fetchCandidates("suspect", groupKey)]);
			setMissed(m);
			setSuspect(s);
		} catch (e) {
			setError(`Couldn't load grouping suggestions (${reasonOf(e)}).`);
		}
	}, [groupKey]);

	useEffect(() => {
		void load();
		// Re-check whenever the resolved group changes, e.g. after a merge or
		// split elsewhere causes ProductPage to reload with a new groupKey.
	}, [load, groupKey]);

	// Both actions re-throw after recording the error, so the confirm button
	// (TwoStepAction) can tell success from failure and only disarm on
	// success — see its own comment for why that distinction matters.
	const confirmMerge = async (candidate: GroupingCandidate): Promise<void> => {
		setBusy(candidate.signature);
		try {
			await postJson("/api/grouping/merge", {
				productIds: candidate.members.map((m) => m.productId),
			});
			onChanged();
			await load();
		} catch (e) {
			setError(`Couldn't confirm this match (${reasonOf(e)}).`);
			throw e;
		} finally {
			setBusy(null);
		}
	};

	const splitOut = async (productId: number): Promise<void> => {
		const key = `unlink:${productId}`;
		setBusy(key);
		try {
			await postJson("/api/grouping/unlink", { productId });
			onChanged();
			await load();
		} catch (e) {
			setError(`Couldn't split this out (${reasonOf(e)}).`);
			throw e;
		} finally {
			setBusy(null);
		}
	};

	const known = new Set(productIds);
	const relevantMissed = (missed ?? [])
		.filter((c) => c.members.some((m) => known.has(m.productId)))
		.map((c) => ({ c, others: c.members.filter((m) => !known.has(m.productId)) }))
		.filter(({ others }) => others.length > 0);
	const relevantSuspect = (suspect ?? []).filter((c) => c.members.some((m) => m.groupKey === groupKey));

	if (relevantMissed.length === 0 && relevantSuspect.length === 0 && !error) {
		return null;
	}

	return (
		<section className="flex flex-col gap-2">
			<h2 className="text-[13px] font-semibold text-ink">Grouping</h2>
			{groupEan && <p className="font-mono text-[10px] text-muted">This group's EAN: {groupEan}</p>}
			{error && <ErrorState message={error} onRetry={() => void load()} />}

			{relevantMissed.map(({ c }) => (
				<div key={c.signature} className="flex flex-col gap-2 rounded-[10px] border border-hairline bg-paper p-3">
					<p className="text-xs text-ink">
						Confirming links every variant below into one group — this can move a variant into a different group than
						it's in now.
					</p>
					{/* The full affected set, not just the ones not yet in this
              group: GroupingReview shows every member with its EAN for the
              same reason — a merge can pick a different group's key than
              this one, moving a product the operator didn't expect to move
              (see the report for the concrete case). */}
					<ul className="flex flex-col gap-1.5">
						{c.members.map((m) => (
							<li key={m.productId} className="flex flex-wrap items-center gap-2 text-xs">
								<span className="rounded-full bg-raised px-2 py-0.5 text-[11px] font-medium text-muted">
									{m.storeId}
								</span>
								<span className="min-w-0 flex-1 truncate text-ink">{m.title}</span>
								{m.ean && <span className="font-mono text-[10px] text-muted">EAN {m.ean}</span>}
								{!known.has(m.productId) && (
									<span className="rounded border border-hairline px-1.5 py-0.5 text-[10px] text-muted">
										not in this group yet
									</span>
								)}
							</li>
						))}
					</ul>
					<div>
						<TwoStepAction
							icon={GitMerge}
							label="Confirm match"
							confirmLabel="Yes, link together"
							busy={busy === c.signature}
							onConfirm={() => confirmMerge(c)}
						/>
					</div>
				</div>
			))}

			{relevantSuspect.map((c) => (
				<div key={c.signature} className="flex flex-col gap-2 rounded-[10px] border border-hairline bg-paper p-3">
					<p className="text-xs text-ink">
						These variants share this group but have different titles — one may not belong.
					</p>
					<ul className="flex flex-col gap-2">
						{c.members.map((m) => (
							<li key={m.productId} className="flex items-center justify-between gap-2 text-xs">
								<span className="flex min-w-0 items-center gap-2">
									<span className="rounded-full bg-raised px-2 py-0.5 text-[11px] font-medium text-muted">
										{m.storeId}
									</span>
									<span className="min-w-0 truncate text-ink">{m.title}</span>
									{m.ean && <span className="font-mono text-[10px] text-muted">EAN {m.ean}</span>}
								</span>
								<TwoStepAction
									icon={Scissors}
									label="Split out"
									confirmLabel="Yes, split out"
									busy={busy === `unlink:${m.productId}`}
									onConfirm={() => splitOut(m.productId)}
									compact
								/>
							</li>
						))}
					</ul>
				</div>
			))}
		</section>
	);
}

const AUTO_DISARM_MS = 8000;

/**
 * A destructive-ish action that requires a second, explicit tap before it
 * fires. The confirming state inverts to ink — weight and fill, not colour,
 * since grouping is not product state.
 *
 * Disarms on success (armed no longer means anything once the action has
 * happened) and, independently, after a short timeout regardless of outcome
 * — an armed irreversible control left untouched is a live trap, not a
 * pause. On failure it stays armed within that window so a genuine retry
 * doesn't need a second confirm; the surrounding ErrorState says why it
 * failed.
 */
function TwoStepAction({
	icon: Icon,
	label,
	confirmLabel,
	busy,
	onConfirm,
	compact = false,
}: {
	icon: LucideIcon;
	label: string;
	confirmLabel: string;
	busy: boolean;
	onConfirm: () => Promise<void>;
	compact?: boolean;
}) {
	const [armed, setArmed] = useState(false);

	useEffect(() => {
		if (!armed) return;
		const timer = setTimeout(() => setArmed(false), AUTO_DISARM_MS);
		return () => clearTimeout(timer);
	}, [armed]);

	const base =
		"inline-flex min-h-tap items-center justify-center gap-1.5 rounded-md text-xs font-medium disabled:opacity-50 sm:h-7 sm:min-h-0";
	const pad = compact ? "px-2.5" : "px-3";

	if (!armed) {
		return (
			<button
				type="button"
				onClick={() => setArmed(true)}
				disabled={busy}
				className={`${base} ${pad} border border-hairline bg-paper text-ink hover:bg-raised`}
			>
				<Icon className="size-3.5" aria-hidden />
				{label}
			</button>
		);
	}

	return (
		<span className="inline-flex items-center gap-1.5">
			<button
				type="button"
				onClick={() => setArmed(false)}
				disabled={busy}
				className={`${base} px-2 text-muted hover:text-ink`}
			>
				Cancel
			</button>
			<button
				type="button"
				onClick={() => {
					void onConfirm()
						.then(() => setArmed(false))
						.catch(() => {
							/* left armed within the timeout window; error is already
                 surfaced by the caller's ErrorState */
						});
				}}
				disabled={busy}
				className={`${base} ${pad} bg-ink font-semibold text-ground hover:opacity-90`}
			>
				<Icon className="size-3.5" aria-hidden />
				{confirmLabel}
			</button>
		</span>
	);
}
